import { z } from "zod";

export type PasswordPolicy = {
  minLength: number;
  maxLength: number;
  minLowercase: number;
  minUppercase: number;
  minNumbers: number;
  minSymbols: number;
  disallowWhitespace?: boolean;
};

export const PASSWORD_POLICY: PasswordPolicy = {
  minLength: 12,
  maxLength: 64,
  minLowercase: 0,
  minUppercase: 0,
  minNumbers: 0,
  minSymbols: 0,
  disallowWhitespace: false,
};

function countMatches(str: string, re: RegExp) {
  return (str.match(re) ?? []).length;
}

export function getPasswordErrors(password: string, policy = PASSWORD_POLICY): string[] {
  const errs: string[] = [];

  if (password.length < policy.minLength) errs.push(`at least ${policy.minLength} characters`);
  if (password.length > policy.maxLength) errs.push(`at most ${policy.maxLength} characters`);

  if (policy.disallowWhitespace && /\s/.test(password)) errs.push("no spaces allowed");

  if (countMatches(password, /[a-z]/g) < policy.minLowercase)
    errs.push(`at least ${policy.minLowercase} lowercase letter(s)`);
  if (countMatches(password, /[A-Z]/g) < policy.minUppercase)
    errs.push(`at least ${policy.minUppercase} uppercase letter(s)`);
  if (countMatches(password, /\d/g) < policy.minNumbers) errs.push(`at least ${policy.minNumbers} number(s)`);
  if (countMatches(password, /[^A-Za-z0-9]/g) < policy.minSymbols) errs.push(`at least ${policy.minSymbols} symbol(s)`);

  return errs;
}

export function isPasswordValid(password: string, policy = PASSWORD_POLICY): boolean {
  return getPasswordErrors(password, policy).length === 0;
}

const PASSWORD_ERROR_MESSAGE = "Password must be a minimum of 12 characters.";

export const passwordSchema = z.string().superRefine((val, ctx) => {
  if (!isPasswordValid(val)) {
    ctx.addIssue({
      code: "custom",
      message: PASSWORD_ERROR_MESSAGE,
    });
  }
});
